import React, { useState, useEffect } from 'react';
import { 
  getAccountsOverview, 
  getIncome, 
  createIncome, 
  deleteIncome, 
  getExpenses, 
  createExpense, 
  deleteExpense, 
  getLedger, 
  getProfitLoss,
  getTripwiseExpenses,
  getDailywiseExpenses
} from '../lib/api';
import api from '../lib/api';
import { formatCurrency } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { DateFilter } from '../components/DateFilter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { 
  Plus, 
  MoreVertical, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Wallet, 
  Receipt, 
  ArrowUpRight, 
  ArrowDownRight,
  Loader2,
  LayoutDashboard,
  PiggyBank,
  CreditCard,
  BookOpen,
  LineChart,
  Plane,
  CalendarDays,
  Search,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#84bd0e', '#383838', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const incomeCategories = [
  { value: 'booking_payment', label: 'Booking Payment' },
  { value: 'commission', label: 'Commission' },
  { value: 'visa_fee', label: 'Visa Fee' },
  { value: 'service_charge', label: 'Service Charge' },
  { value: 'other', label: 'Other' },
];

const expenseCategories = [
  { value: 'vendor_payment', label: 'Vendor Payment' },
  { value: 'hotel_payment', label: 'Hotel Payment' },
  { value: 'airline_payment', label: 'Airline Payment' },
  { value: 'office_expense', label: 'Office Expense' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'salary', label: 'Salary' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'travel', label: 'Travel' },
  { value: 'other', label: 'Other' },
];

const getCategoryLabel = (category, isExpense = false) => {
  const categories = isExpense ? expenseCategories : incomeCategories;
  return categories.find(c => c.value === category)?.label || category;
};

export const Accounts = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [tripwiseData, setTripwiseData] = useState([]);
  const [dailywiseData, setDailywiseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plPeriod, setPlPeriod] = useState('monthly');
  const [dailyDateRange, setDailyDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });
  
  // Dialog states
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [incomeForm, setIncomeForm] = useState({
    category: 'booking_payment',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  
  const [expenseForm, setExpenseForm] = useState({
    category: 'vendor_payment',
    amount: '',
    description: '',
    vendor_name: '',
    trip_code: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Trip code search state
  const [tripCodeSearch, setTripCodeSearch] = useState('');
  const [searchedTrip, setSearchedTrip] = useState(null);
  const [searchingTrip, setSearchingTrip] = useState(false);
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    loadData();
    loadTrips();
  }, [activeTab, plPeriod, dailyDateRange]);

  const loadTrips = async () => {
    try {
      const { data } = await api.get('/trips');
      setTrips(data || []);
    } catch (error) {
      console.error('Failed to load trips');
    }
  };

  // Search trip by code
  const searchTripByCode = async (code) => {
    if (!code || code.length < 3) return;
    setSearchingTrip(true);
    try {
      const { data } = await api.get(`/trips/code/${code.toUpperCase()}`);
      setSearchedTrip(data);
      toast.success(`Found: ${data.trip_name}`);
    } catch (error) {
      setSearchedTrip(null);
      if (error.response?.status === 404) {
        toast.error('Trip code not found');
      }
    } finally {
      setSearchingTrip(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const { data } = await getAccountsOverview();
        setOverview(data);
      } else if (activeTab === 'income') {
        const { data } = await getIncome();
        setIncome(data);
      } else if (activeTab === 'expenses') {
        const { data } = await getExpenses();
        setExpenses(data);
      } else if (activeTab === 'ledger') {
        const { data } = await getLedger();
        setLedger(data);
      } else if (activeTab === 'profit-loss') {
        const { data } = await getProfitLoss(plPeriod);
        setProfitLoss(data);
      } else if (activeTab === 'tripwise') {
        const { data } = await getTripwiseExpenses();
        setTripwiseData(data);
      } else if (activeTab === 'dailywise') {
        const { data } = await getDailywiseExpenses(dailyDateRange);
        setDailywiseData(data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIncome = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createIncome({
        ...incomeForm,
        amount: parseFloat(incomeForm.amount),
      });
      toast.success('Income recorded successfully');
      setIncomeDialogOpen(false);
      setIncomeForm({
        category: 'booking_payment',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record income');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createExpense({
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
      });
      toast.success('Expense recorded successfully');
      setExpenseDialogOpen(false);
      setExpenseForm({
        category: 'vendor_payment',
        amount: '',
        description: '',
        vendor_name: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm('Delete this income record?')) return;
    try {
      await deleteIncome(id);
      toast.success('Income deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense record?')) return;
    try {
      await deleteExpense(id);
      toast.success('Expense deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading && !overview && !income.length && !expenses.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="accounts-container" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Accounts</h1>
          <p className="text-muted-foreground mt-1">Manage income, expenses, and financial reports</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
            <DialogTrigger asChild>
              <Button className="text-white" style={{ backgroundColor: '#383838' }}>
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Add Income
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-outfit">Record Income</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateIncome} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={incomeForm.category} onValueChange={(v) => setIncomeForm({ ...incomeForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeCategories.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount ($) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={incomeForm.amount}
                      onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={incomeForm.date}
                      onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Input
                    value={incomeForm.description}
                    onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                    placeholder="e.g., Booking payment from John Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={incomeForm.notes}
                    onChange={(e) => setIncomeForm({ ...incomeForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIncomeDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Record Income
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                <ArrowDownRight className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-outfit">Record Expense</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateExpense} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount ($) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Input
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    placeholder="e.g., Hotel payment for Maldives booking"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor Name</Label>
                  <Input
                    value={expenseForm.vendor_name}
                    onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
                    placeholder="e.g., Marriott Hotels"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={expenseForm.notes}
                    onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Record Expense
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <LayoutDashboard className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="income" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <PiggyBank className="w-4 h-4 mr-2" />
            Income
          </TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <CreditCard className="w-4 h-4 mr-2" />
            Expenses
          </TabsTrigger>
          <TabsTrigger value="ledger" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <BookOpen className="w-4 h-4 mr-2" />
            Ledger
          </TabsTrigger>
          <TabsTrigger value="profit-loss" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <LineChart className="w-4 h-4 mr-2" />
            P&L Report
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="tripwise" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
                <Plane className="w-4 h-4 mr-2" />
                Trip-wise
              </TabsTrigger>
              <TabsTrigger value="dailywise" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
                <CalendarDays className="w-4 h-4 mr-2" />
                Daily-wise
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-600 font-outfit">{formatCurrency(overview?.total_income)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#383838' }}>
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600 font-outfit">{formatCurrency(overview?.total_expenses)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Net Profit</p>
                    <p className={`text-2xl font-bold font-outfit ${(overview?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(overview?.net_profit)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#84bd0e' }}>
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Booking Revenue</p>
                    <p className="text-2xl font-bold text-foreground font-outfit">{formatCurrency(overview?.booking_income)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Income by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {overview?.income_by_category?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={overview.income_by_category}
                          dataKey="total"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ category, total }) => `${getCategoryLabel(category)}: ${formatCurrency(total)}`}
                        >
                          {overview.income_by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      No income data yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Expenses by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {overview?.expenses_by_category?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={overview.expenses_by_category}
                          dataKey="total"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ category, total }) => `${getCategoryLabel(category, true)}: ${formatCurrency(total)}`}
                        >
                          {overview.expenses_by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[(index + 3) % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      No expense data yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Transactions */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.recent_transactions?.length > 0 ? (
                <div className="space-y-3">
                  {overview.recent_transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tx.type === 'income' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {tx.type === 'income' ? (
                            <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <ArrowDownRight className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{tx.description}</p>
                          <p className="text-sm text-muted-foreground">{getCategoryLabel(tx.category, tx.type === 'expense')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${tx.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">{tx.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No transactions yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income" className="mt-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Income Records</CardTitle>
              <ExportImportButtons module="accounts-income" onImportSuccess={loadData} />
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Category</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold text-right">Amount</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {income.length > 0 ? (
                    income.map((item) => (
                      <TableRow key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                        <TableCell>{item.date}</TableCell>
                        <TableCell>
                          <Badge className="bg-emerald-50 text-emerald-700">{getCategoryLabel(item.category)}</Badge>
                        </TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">+{formatCurrency(item.amount)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDeleteIncome(item.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No income records. Click &quot;Add Income&quot; to record one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="mt-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Expense Records</CardTitle>
              <ExportImportButtons module="accounts-expenses" onImportSuccess={loadData} />
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Category</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold">Vendor</TableHead>
                    <TableHead className="font-semibold text-right">Amount</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.length > 0 ? (
                    expenses.map((item) => (
                      <TableRow key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                        <TableCell>{item.date}</TableCell>
                        <TableCell>
                          <Badge className="bg-red-50 text-red-700">{getCategoryLabel(item.category, true)}</Badge>
                        </TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.vendor_name || '-'}</TableCell>
                        <TableCell className="text-right font-semibold text-red-600">-{formatCurrency(item.amount)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDeleteExpense(item.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No expense records. Click &quot;Add Expense&quot; to record one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ledger Tab */}
        <TabsContent value="ledger" className="mt-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Transaction Ledger</CardTitle>
              <ExportImportButtons module="accounts-ledger" onImportSuccess={loadData} />
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">Category</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold text-right">Debit</TableHead>
                    <TableHead className="font-semibold text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.length > 0 ? (
                    ledger.map((item) => (
                      <TableRow key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                        <TableCell>{item.date}</TableCell>
                        <TableCell>
                          <Badge className={item.type === 'income' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>
                            {item.type}
                          </Badge>
                        </TableCell>
                        <TableCell>{getCategoryLabel(item.category, item.type === 'expense')}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right text-red-600">
                          {item.type === 'expense' ? formatCurrency(item.amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-emerald-600">
                          {item.type === 'income' ? formatCurrency(item.amount) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No transactions in ledger yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="profit-loss" className="mt-6 space-y-6">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Profit & Loss Report</CardTitle>
              <Select value={plPeriod} onValueChange={setPlPeriod}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              {profitLoss?.summary && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(profitLoss.summary.total_income)}</p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(profitLoss.summary.total_expenses)}</p>
                  </div>
                  <div className={`p-4 rounded-lg text-center ${profitLoss.summary.total_profit >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                    <p className="text-sm text-muted-foreground">Net Profit</p>
                    <p className={`text-2xl font-bold ${profitLoss.summary.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(profitLoss.summary.total_profit)}
                    </p>
                  </div>
                </div>
              )}

              {/* Chart */}
              <div className="h-80">
                {profitLoss?.data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={profitLoss.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="period" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} tickFormatter={(value) => `$${value / 1000}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="income" name="Income" fill="#383838" />
                      <Bar dataKey="expenses" name="Expenses" fill="#ef4444" />
                      <Bar dataKey="profit" name="Profit" fill="#84bd0e" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No data available for the selected period
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Table */}
          {profitLoss?.data?.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Period Details</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800">
                      <TableHead className="font-semibold">Period</TableHead>
                      <TableHead className="font-semibold text-right">Income</TableHead>
                      <TableHead className="font-semibold text-right">Expenses</TableHead>
                      <TableHead className="font-semibold text-right">Profit/Loss</TableHead>
                      <TableHead className="font-semibold text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitLoss.data.map((row) => (
                      <TableRow key={row.period} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                        <TableCell className="font-medium">{row.period}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatCurrency(row.income)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(row.expenses)}</TableCell>
                        <TableCell className={`text-right font-semibold ${row.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(row.profit)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.income > 0 ? `${((row.profit / row.income) * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Trip-wise Expenditure Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="tripwise" className="mt-6 space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>
                  Trip-wise Profit & Loss Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800">
                      <TableHead className="font-semibold">Trip Code</TableHead>
                      <TableHead className="font-semibold">Trip Name</TableHead>
                      <TableHead className="font-semibold">Destination</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold text-center">Bookings</TableHead>
                      <TableHead className="font-semibold text-right">Income</TableHead>
                      <TableHead className="font-semibold text-right">Expenses</TableHead>
                      <TableHead className="font-semibold text-right">Profit/Loss</TableHead>
                      <TableHead className="font-semibold text-right">Margin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tripwiseData.length > 0 ? (
                      tripwiseData.map((trip) => (
                        <TableRow key={trip.trip_id || trip.booking_id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                          <TableCell>
                            {trip.trip_code ? (
                              <Badge variant="outline" className="font-mono text-xs bg-[#84bd0e]/10 text-[#84bd0e] border-[#84bd0e]/30">
                                {trip.trip_code}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="font-medium">{trip.trip_name || trip.customer_name || 'N/A'}</TableCell>
                          <TableCell>{trip.destination}</TableCell>
                          <TableCell>{trip.trip_date || trip.travel_date ? new Date(trip.trip_date || trip.travel_date).toLocaleDateString('en-IN') : 'N/A'}</TableCell>
                          <TableCell className="text-center">{trip.total_bookings || 0}</TableCell>
                          <TableCell className="text-right text-emerald-600">{formatCurrency(trip.total_payments || trip.total_amount || 0)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(trip.total_expenses)}</TableCell>
                          <TableCell className={`text-right font-semibold ${trip.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(trip.profit)}
                          </TableCell>
                          <TableCell className="text-right">
                            {trip.profit_margin ? `${trip.profit_margin}%` : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No trip data available. Create trips and link expenses using Trip Code.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            {tripwiseData.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Total Trips</p>
                    <p className="text-2xl font-bold text-foreground font-outfit">
                      {tripwiseData.length}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-600 font-outfit">
                      {formatCurrency(tripwiseData.reduce((sum, t) => sum + parseFloat(t.total_payments || t.total_amount || 0), 0))}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600 font-outfit">
                      {formatCurrency(tripwiseData.reduce((sum, t) => sum + parseFloat(t.total_expenses || 0), 0))}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Net Profit</p>
                    <p className="text-2xl font-bold text-emerald-600 font-outfit">
                      {formatCurrency(tripwiseData.reduce((sum, t) => sum + parseFloat(t.profit || 0), 0))}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        )}

        {/* Daily-wise Expenditure Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="dailywise" className="mt-6 space-y-6">
            {/* Date Range Filter */}
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={dailyDateRange.start_date}
                      onChange={(e) => setDailyDateRange({ ...dailyDateRange, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={dailyDateRange.end_date}
                      onChange={(e) => setDailyDateRange({ ...dailyDateRange, end_date: e.target.value })}
                    />
                  </div>
                  <Button onClick={loadData} className="bg-slate-900 hover:bg-slate-800">
                    Apply Filter
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            {dailywiseData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Period Total</p>
                    <p className="text-2xl font-bold text-red-600 font-outfit">
                      {formatCurrency(dailywiseData.period_total)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {dailywiseData.start_date} to {dailywiseData.end_date}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Total Days</p>
                    <p className="text-2xl font-bold text-foreground font-outfit">
                      {dailywiseData.daily_data?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">with transactions</p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-1">Daily Average</p>
                    <p className="text-2xl font-bold text-foreground font-outfit">
                      {formatCurrency(dailywiseData.daily_data?.length > 0 ? dailywiseData.period_total / dailywiseData.daily_data.length : 0)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">per day</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Category Breakdown */}
            {dailywiseData?.category_breakdown?.length > 0 && (
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Category Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {dailywiseData.category_breakdown.map((cat, index) => (
                      <div key={cat.category} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <p className="text-sm text-muted-foreground capitalize">{getCategoryLabel(cat.category, true)}</p>
                        <p className="text-lg font-semibold text-foreground">{formatCurrency(cat.total)}</p>
                        <p className="text-xs text-muted-foreground">{cat.count} transactions</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Data Table */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-outfit text-lg" style={{ color: '#84bd0e' }}>Daily Expenses</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Categories</TableHead>
                      <TableHead className="font-semibold text-right">Transactions</TableHead>
                      <TableHead className="font-semibold text-right">Total Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailywiseData?.daily_data?.length > 0 ? (
                      dailywiseData.daily_data.map((day) => (
                        <TableRow key={day.date} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                          <TableCell className="font-medium">
                            {new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {day.categories?.split(',').map((cat, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {getCategoryLabel(cat.trim(), true)}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{day.transaction_count}</TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {formatCurrency(day.total_amount)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No expenses found for the selected date range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
